import { Spotlight, spotlight, type SpotlightActionData, type SpotlightActionGroupData } from '@mantine/spotlight';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconArrowRight, IconBuildingFactory2, IconClipboardList, IconClock, IconHistory, IconPlus, IconSearch, IconTicket, IconUsers,
  IconVideo, IconDoorEnter,
} from '@tabler/icons-react';
import { router } from '@/app/router';
import { STATIC_ROUTES } from '@/shared/lib/staticRoutes';
import { denialReason, entryByPath } from '@/app/layouts/navAccess';
import { useAccessContext } from '@/app/layouts/useContestoAccesso';
import { openMaeSpace, useMaeSpaceAccess } from '@/features/maespace/useMaeSpace';
import { openExternalOrWarn, roomKindLabel, roomTitle, useMyRooms } from '@/features/maespace/MyRoomsCard.utils';
import { useQuery } from '@tanstack/react-query';
import { subscribeSearchText, writeSearchText, searchText } from '@/shared/lib/ricercaGlobale';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { fetchList } from '@/shared/api/jsonapi/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { getAllSearchEntries, subscribeSearchRegistry, type SearchEntry } from '@/shared/lib/searchRegistry';

import { leggiChiave, writeKey } from '@/shared/lib/storage';
import { STORAGE_KEYS } from '@/shared/lib/constants';

const EMPTY_ENTRIES: SearchEntry[] = [];

const RECENT_KEY = STORAGE_KEYS.spotlightRecent;
const RECENT_MAX = 5;

interface RecentEntry { id: string; label: string; to: string; iconName?: string }

/* Pagine spostate: il recente porterebbe il nome vecchio. */
const MOVED_PATHS = new Set(['/tasks/wbs']);

function loadRecent(): RecentEntry[] {
  try {
    const list: RecentEntry[] = JSON.parse(leggiChiave(RECENT_KEY) ?? '[]');
    return list.filter((e) => !MOVED_PATHS.has(e.to));
  } catch { return []; }
}
function pushRecent(entry: RecentEntry) {
  try {
    const list = loadRecent().filter((e) => e.to !== entry.to);
    list.unshift(entry);
    writeKey(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch { /* ignore */ }
}

interface NavSearch extends Denormalized { type: string; title?: string; name?: string; firstName?: string; lastName?: string; email?: string }

export function SpotlightProvider({ children }: { children: ReactNode }) {
  // NB: questo provider è montato FUORI dal RouterProvider, quindi `useNavigate()`
  // non avrebbe contesto e il click non navigherebbe. Usiamo il singleton `router`.
  /* Il testo vive nello store condiviso: la lente in barra scrive lì, e qui si legge. Con due
     stati paralleli — uno per il campo in alto, uno per questo pannello — basta una digitazione
     veloce perché divergano, e i risultati sarebbero quelli di un attimo prima. */
  const query = useSyncExternalStore(subscribeSearchText, searchText, searchText);
  const setQuery = writeSearchText;
  const [debouncedQuery] = useDebouncedValue(query, 250);
  const [recent, setRecent] = useState<RecentEntry[]>([]);

  useEffect(() => { setRecent(loadRecent()); }, []);

  // Subscribe al registry dinamico: ogni pagina che usa `useRegisterSearch`
  // inietta voci visibili qui (gruppo "Su questa pagina").
  const dynamicEntries = useSyncExternalStore<SearchEntry[]>(
    subscribeSearchRegistry,
    getAllSearchEntries,
    () => EMPTY_ENTRIES,
  );

  /* La stessa regola del menu, per non offrire da qui le pagine che la colonna nasconde. */
  const access = useAccessContext();
  const maespace = useMaeSpaceAccess();
  /* Le stanze si leggono alla **prima apertura** della palette, non al caricamento dell'app: chi
     non usa mai lo Spotlight non paga la chiamata. Poi restano in cache (la stessa query della
     home), quindi le aperture successive le hanno subito. */
  const [everOpened, setEverOpened] = useState(false);
  const roomsQ = useMyRooms(maespace.moduleOn && everOpened);

  const go = useCallback((to: string, label: string) => {
    spotlight.close();
    pushRecent({ id: to, to, label });
    setRecent(loadRecent());
    void router.navigate({ to });
  }, []);

  // Live search: tasks / projects / users via API (best-effort; failures = empty)
  const enabled = debouncedQuery.trim().length >= 2;
  const tasksQ = useQuery({
    queryKey: ['spotlight-tasks', debouncedQuery],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const res = await fetchList<NavSearch>('tasks', {
          filter: { title: { like: debouncedQuery } },
          page: { size: 5 },
        });
        return res.data;
      } catch { return []; }
    },
  });
  const projectsQ = useQuery({
    queryKey: ['spotlight-projects', debouncedQuery],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const res = await fetchList<NavSearch>('projects', {
          filter: { name: { like: debouncedQuery } },
          page: { size: 5 },
        });
        return res.data;
      } catch { return []; }
    },
  });
  const usersQ = useQuery({
    queryKey: ['spotlight-users', debouncedQuery],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const res = await fetchList<NavSearch>('users', {
          filter: { email: { like: debouncedQuery } },
          page: { size: 5 },
        });
        return res.data;
      } catch { return []; }
    },
  });

  const actionGroups = useMemo<SpotlightActionGroupData[]>(() => {
    const groups: SpotlightActionGroupData[] = [];

    /*
     * Una scorciatoia verso una pagina che l'utente non puo' aprire e' peggio di una voce di menu
     * di troppo: la si offre in cima, prima di tutto, e chi la sceglie finisce nel pannello del
     * divieto. Quindi anche qui passa il predicato — e per le stesse tre righe passano i
     * «Recenti», che possono puntare a una pagina che il cambio di ruolo ha portato via.
     */
    const reachable = (to: string) => denialReason(entryByPath(to), access) === null;

    /* La destinazione sta accanto all'azione, non dedotta dall'id: e' quello che il filtro deve
       guardare, e tenerla nel dato evita la catena di ternari che sarebbe l'alternativa. */
    const quickActions: Array<SpotlightActionData & { path: string }> = [
      {
        id: 'qa-new-task',
        path: '/tasks',
        label: 'Nuovo task',
        description: 'Apri il form di creazione task',
        leftSection: <IconPlus size={18} />,
        keywords: ['task', 'nuovo', 'crea', 'create'],
        onClick: () => {
          spotlight.close();
          void router.navigate({ to: '/tasks' });
          // I componenti di lista mostrano il pulsante "Nuovo": rinvio l'utente lì.
        },
      },
      {
        id: 'qa-clock',
        path: '/',
        label: 'Timbratura',
        description: 'Apri il widget timbratura nella home',
        leftSection: <IconClock size={18} />,
        keywords: ['clock', 'timbra', 'attendance', 'presenza'],
        onClick: () => go('/', 'Home'),
      },
      {
        id: 'qa-ticket',
        path: '/tickets',
        label: 'Nuovo ticket',
        description: 'Apri il centro assistenza',
        leftSection: <IconTicket size={18} />,
        keywords: ['ticket', 'help', 'supporto'],
        onClick: () => go('/tickets', 'Help desk'),
      },
    ];

    /* «Apri in MaeSpace» non ha una rotta da far passare a `reachable`: il suo cancello è modulo +
       permesso + niente impersonificazione (`useMaeSpaceAccess`). In impersonificazione non si
       offre affatto: nella palette non c'è un posto dove scrivere il motivo, e il menu del profilo
       lo dice. */
    const maespaceActions: SpotlightActionData[] = maespace.canOpen && !maespace.disabledReason ? [{
      id: 'qa-maespace',
      label: 'Apri in MaeSpace',
      description: 'Entra in MaeSpace senza un secondo login',
      leftSection: <IconVideo size={18} />,
      keywords: ['maespace', 'videochiamata', 'call', 'riunione', 'stanza'],
      onClick: () => { spotlight.close(); void openMaeSpace(); },
    }] : [];

    // QUICK ACTIONS — sempre in cima
    groups.push({
      group: 'Azioni rapide',
      actions: [...quickActions.filter((a) => reachable(a.path)), ...maespaceActions],
    });

    /* STANZE MAESPACE — a modulo spento non ci sono (la query non parte). Una lettura fallita
       qui non si dice: la palette non ha un posto per l'errore, e la home lo mostra. */
    const roomActions: SpotlightActionData[] = (roomsQ.data ?? []).map((r) => {
      const title = roomTitle(r);
      return {
        id: `room-${r.ownerType}-${r.ownerId}`,
        label: title,
        description: [roomKindLabel(r.ownerType), r.project?.code].filter(Boolean).join(' · '),
        leftSection: <IconDoorEnter size={18} />,
        keywords: ['maespace', 'stanza', roomKindLabel(r.ownerType), r.project?.code ?? ''].filter(Boolean),
        onClick: () => { spotlight.close(); openExternalOrWarn(r.url); },
      };
    });
    if (roomActions.length > 0) groups.push({ group: 'Stanze MaeSpace', actions: roomActions });

    // RECENT
    if (recent.length > 0) {
      groups.push({
        group: 'Recenti',
        actions: recent.filter((r) => reachable(r.to)).map((r) => ({
          id: `recent-${r.id}`,
          label: r.label,
          leftSection: <IconHistory size={18} />,
          onClick: () => go(r.to, r.label),
        })),
      });
    }

    // ENTRIES REGISTRATE DALLA PAGINA CORRENTE — bottoni, sezioni, titoli
    // esposti dalle pagine via `useRegisterSearch`.
    if (dynamicEntries.length > 0) {
      // Raggruppo per `group` (default "Su questa pagina")
      const byGroup = new Map<string, SearchEntry[]>();
      for (const e of dynamicEntries) {
        const g = e.group ?? 'Su questa pagina';
        const arr = byGroup.get(g) ?? [];
        arr.push(e);
        byGroup.set(g, arr);
      }
      for (const [groupName, entries] of byGroup) {
        groups.push({
          group: groupName,
          actions: entries.map((e) => ({
            id: `reg-${e.id}`,
            label: e.label,
            description: e.description,
            leftSection: <IconArrowRight size={18} />,
            keywords: e.keywords,
            onClick: () => {
              if (e.onSelect) {
                spotlight.close();
                e.onSelect();
              } else if (e.to) {
                go(e.to, e.label);
              }
            },
          })),
        });
      }
    }

    // SEARCH RESULTS (only when typing >= 2)
    if (enabled) {
      const taskActions: SpotlightActionData[] = (tasksQ.data ?? []).map((t) => ({
        id: `task-${t.id}`,
        label: t.title ?? '(task senza titolo)',
        description: `Task · ${t.id.slice(0, 8)}`,
        leftSection: <IconClipboardList size={18} />,
        keywords: ['task', t.title ?? ''].filter(Boolean),
        onClick: () => go(`/tasks`, t.title ?? 'Task'),
      }));
      const projActions: SpotlightActionData[] = (projectsQ.data ?? []).map((p) => ({
        id: `project-${p.id}`,
        label: p.name ?? '(progetto senza nome)',
        description: `Progetto · ${p.id.slice(0, 8)}`,
        leftSection: <IconBuildingFactory2 size={18} />,
        onClick: () => go(`/projects/${p.id}`, p.name ?? 'Progetto'),
      }));
      const userActions: SpotlightActionData[] = (usersQ.data ?? []).map((u) => {
        const full = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '(utente)';
        return {
          id: `user-${u.id}`,
          label: full,
          description: u.email ?? `Utente · ${u.id.slice(0, 8)}`,
          leftSection: <IconUsers size={18} />,
          onClick: () => go(`/users/${u.id}`, full),
        };
      });
      if (taskActions.length) groups.push({ group: 'Task', actions: taskActions });
      if (projActions.length) groups.push({ group: 'Progetti', actions: projActions });
      if (userActions.length) groups.push({ group: 'Utenti', actions: userActions });
    }

    /*
     * STATIC NAV — filtrata con **la stessa regola del menu** (`navAccess`).
     *
     * Era l'elenco intero, per tutti: nascondere una voce dalla colonna e lasciarla qui non è
     * mezza protezione, è nessuna — chi cerca la trova comunque, e la trova senza il contesto che
     * il menu le darebbe. Con novantacinque voci gatate il divario diventava la strada
     * principale per arrivare a una pagina che poi si rifiuta.
     *
     * Il requisito non è duplicato in `staticRoutes.ts`: si legge dall'albero per prefisso, quindi
     * `/projects/gantt` prende quello di `/projects` e le due liste non possono divergere.
     */
    groups.push({
      group: 'Navigazione',
      actions: STATIC_ROUTES
        .filter((r) => denialReason(entryByPath(r.to), access) === null)
        .map((r) => ({
          id: `nav-${r.to}`,
          label: r.label,
          description: r.section,
          leftSection: <r.icon size={18} />,
          keywords: r.keywords,
          onClick: () => go(r.to, r.label),
        })),
    });

    return groups;
  }, [recent, enabled, tasksQ.data, projectsQ.data, usersQ.data, dynamicEntries, go, access, maespace.canOpen, maespace.disabledReason, roomsQ.data]);

  return (
    <>
      {children}
      <Spotlight
        actions={actionGroups}
        nothingFound="Nessun risultato"
        highlightQuery
        searchProps={{
          leftSection: <IconSearch size={20} />,
          placeholder: 'Cerca pagine, task, progetti, utenti…',
          value: query,
          onChange: (e) => setQuery(e.currentTarget.value),
        }}
        onSpotlightOpen={() => setEverOpened(true)}
        shortcut={['mod + K', 'mod + P', '/']}
        scrollable
        maxHeight={520}
      />
    </>
  );
}

// Il catalogo pagine vive in `shared/lib/staticRoutes` (dato puro): qui resta solo il
// re-export, così chi importava da questo modulo continua a funzionare.
export { STATIC_ROUTES, type StaticRoute } from '@/shared/lib/staticRoutes';
